import { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useFloatingPanel } from '../../hooks/useFloatingPanel.js';

const PROFILE_LABELS = {
  TIENDA: 'Tienda', SUBGERENTE_LAST_MILE: 'Subgerente Last Mile', GERENTE: 'Gerente Last Mile',
  OMNICANAL: 'Omnicanal', ADMINISTRADOR: 'Administrador', PROVEEDOR: 'Proveedor',
};

export default function ProfileMenu({ session, onLogout, external }) {
  const { open, setOpen, pos, triggerRef, panelRef } = useFloatingPanel();
  const trigger = useRef(null);
  const panel = useRef(null);
  const id = useId();
  const { pathname } = useLocation();
  const fullName = `${session.nombre || ''} ${session.apellido || ''}`.trim();
  const store = session.tiendaDescripcion || '';
  const genericStore = !session.apellido && (!session.nombre || session.nombre === store);
  const name = session.perfil === 'TIENDA'
    ? genericStore ? `${store} (${session.usuario || ''})` : `${fullName} (${store})`
    : fullName || session.usuario || 'Mi perfil';
  const profile = external ? `Proveedor · ${session.proveedorDescripcion || 'Sin proveedor asignado'}`
    : PROFILE_LABELS[session.perfil] || session.perfil;

  useEffect(() => { setOpen(false); }, [pathname, setOpen]);
  useEffect(() => {
    if (!open) return undefined;
    panel.current?.querySelector('[role="menuitem"]')?.focus();
    function dismiss(event) {
      if (event.key === 'Escape') {
        event.preventDefault(); setOpen(false); trigger.current?.focus();
      }
    }
    function leave(event) {
      if (!panel.current?.contains(event.target) && !trigger.current?.contains(event.target)) setOpen(false);
    }
    document.addEventListener('keydown', dismiss);
    document.addEventListener('focusin', leave);
    return () => { document.removeEventListener('keydown', dismiss); document.removeEventListener('focusin', leave); };
  }, [open, setOpen]);

  function navigateMenu(event) {
    const keys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
    if (!keys.includes(event.key)) return;
    event.preventDefault();
    const items = [...panel.current.querySelectorAll('[role="menuitem"]')];
    const index = items.indexOf(document.activeElement);
    const next = event.key === 'Home' ? 0 : event.key === 'End' ? items.length - 1
      : (index + (event.key === 'ArrowDown' ? 1 : -1) + items.length) % items.length;
    items[next]?.focus();
  }

  return <>
    <button type="button" className="navigation-profile" ref={node => { trigger.current = node; triggerRef(node); }}
      aria-haspopup="menu" aria-expanded={open} aria-controls={open ? id : undefined}
      onClick={() => setOpen(!open)} onKeyDown={event => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') { event.preventDefault(); setOpen(true); }
      }}>
      <span className="navigation-avatar" aria-hidden="true">{name.slice(0, 1).toUpperCase()}</span>
      <span className="navigation-identity"><strong>{name}</strong><small>{profile}</small></span>
      <span className="material-symbols-rounded" aria-hidden="true">expand_more</span>
    </button>
    {open && createPortal(<div ref={node => { panel.current = node; panelRef(node); }}
      id={id} role="menu" aria-label="Mi perfil" className="navigation-profile-menu" onKeyDown={navigateMenu}
      style={{ position: 'fixed', left: pos?.left, top: pos?.top, zIndex: pos?.zIndex, maxHeight: pos?.availableHeight }}>
      <Link role="menuitem" to="/mi-cuenta" onClick={() => setOpen(false)}>
        <span className="material-symbols-rounded" aria-hidden="true">manage_accounts</span>Mi cuenta
      </Link>
      <hr />
      <button type="button" role="menuitem" className="btn btn-danger-primary navigation-logout" onClick={() => { setOpen(false); onLogout(); }}>
        <span className="material-symbols-rounded" aria-hidden="true">logout</span>Salir
      </button>
    </div>, document.body)}
  </>;
}
